import React, { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { useToast } from '../contexts/ToastContext';
import { Link } from 'react-router-dom';
import DataFetchState from '../components/ui/DataFetchState';
import AnimatedEmptyState from '../components/ui/AnimatedEmptyState';
import {
  Plus,
  IndianRupee,
  Calendar,
  Tag,
  Receipt,
  TrendingDown,
  Filter,
  Search,
  Download,
  Edit2,
  Trash2,
  X,
  Save
} from 'lucide-react';

const ExpenseTracker = () => {
  const { user } = useAuth();
  const {
    expenses: allExpenses,
    bookings,
    offlineJobs,
    createExpense,
    updateExpense,
    deleteExpense,
    userDataLoading,
    userDataError,
    refreshUserData,
  } = useData();
  const toast = useToast();
  
  // Filter expenses for current user
  const userId = user?.uid || user?.id;
  const expenses = useMemo(() => {
    console.log('🔍 Filtering expenses for userId:', userId);
    console.log('🔍 All expenses:', allExpenses);
    const filtered = allExpenses.filter(e => {
      console.log('  - Expense userId:', e.userId, 'caregiverId:', e.caregiverId, 'matches:', e.userId === userId || e.caregiverId === userId);
      return e.userId === userId || e.caregiverId === userId;
    });
    console.log('🔍 Filtered expenses:', filtered);
    return filtered;
  }, [allExpenses, userId]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    category: 'Food & Supplies',
    description: '',
    amount: '',
    paymentMethod: 'cash',
    bookingId: '',
    bookingRef: '',
    notes: ''
  });

  const categories = [
    'Food & Supplies',
    'Transportation',
    'Medical',
    'Equipment',
    'Grooming Supplies',
    'Training Materials',
    'Marketing',
    'Insurance',
    'Utilities',
    'Other'
  ];

  // Get user's bookings (filter by caregiverId)
  const userBookings = useMemo(() => {
    console.log('📅 Filtering bookings for caregiverId:', userId);
    console.log('📅 All bookings:', bookings);
    const filtered = bookings.filter(b => {
      console.log('  - Booking caregiverId:', b.caregiverId, 'matches:', b.caregiverId === userId);
      return b.caregiverId === userId;
    });
    console.log('📅 Filtered bookings:', filtered);
    return filtered;
  }, [bookings, userId]);

  // Get user's offline jobs
  const userOfflineJobs = useMemo(() => {
    console.log('💼 Filtering offline jobs for userId:', userId);
    console.log('💼 All offline jobs:', offlineJobs);
    const filtered = offlineJobs.filter(j => {
      console.log('  - Job userId:', j.userId, 'matches:', j.userId === userId);
      return j.userId === userId;
    });
    console.log('💼 Filtered offline jobs:', filtered);
    return filtered;
  }, [offlineJobs, userId]);

  // Combine bookings and offline jobs for the dropdown
  const allJobs = useMemo(() => {
    const items = [];
    
    // Add bookings
    userBookings.forEach(booking => {
      const petName = booking.petName || 'Pet';
      const serviceName = booking.serviceName || booking.service || 'Service';
      const startDate = booking.startDate || booking.date;
      const bookingNumber = booking.bookingNumber || booking.id.substring(0, 8).toUpperCase();
      items.push({
        id: booking.id,
        type: 'booking',
        label: `🔵 Booking #${bookingNumber} - ${petName} - ${serviceName}`,
        date: startDate,
        reference: `Booking #${bookingNumber} - ${petName} - ${serviceName}`
      });
    });
    
    // Add offline jobs
    userOfflineJobs.forEach(job => {
      const clientName = job.clientName || 'Client';
      const petName = job.petName || 'Pet';
      const serviceName = job.serviceType || 'Service';
      const startDate = job.startDate || job.date || job.createdAt;
      const jobNumber = job.jobNumber || job.id.substring(0, 8).toUpperCase();
      items.push({
        id: job.id,
        type: 'offlineJob',
        label: `🟢 Offline Job #${jobNumber} - ${clientName} - ${petName}`,
        date: startDate,
        reference: `Offline Job #${jobNumber} - ${clientName} - ${petName}`
      });
    });
    
    // Sort by date (most recent first)
    items.sort((a, b) => {
      const dateA = new Date(a.date || 0);
      const dateB = new Date(b.date || 0);
      return dateB - dateA;
    });
    
    console.log('📋 Combined jobs for dropdown:', items);
    return items;
  }, [userBookings, userOfflineJobs]);

  const totalExpenses = expenses.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);
  const taggedExpenses = expenses.filter(exp => exp.bookingId).reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);
  const untaggedExpenses = totalExpenses - taggedExpenses;

  const filteredExpenses = expenses.filter(exp => {
    const matchesSearch = exp.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          exp.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || exp.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('💾 Submitting expense form...');
    
    const expenseData = {
      date: formData.date,
      category: formData.category,
      description: formData.description,
      amount: parseFloat(formData.amount),
      paymentMethod: formData.paymentMethod,
      bookingId: formData.bookingId || '',
      bookingRef: formData.bookingRef || '',
      notes: formData.notes || ''
    };

    console.log('📝 Expense data:', expenseData);

    if (editingExpense) {
      // Update existing expense
      const result = await updateExpense(editingExpense.id, expenseData);
      console.log('✏️ Update result:', result);
      if (result.success) {
        toast.success('Expense updated successfully!');
        resetForm();
      } else {
        toast.error(`Failed to update expense: ${result.error || 'Unknown error'}`);
      }
    } else {
      // Create new expense
      const result = await createExpense(expenseData);
      console.log('➕ Create result:', result);
      console.log('📊 Current expenses count:', expenses.length);
      console.log('📊 All expenses count:', allExpenses.length);
      if (result.success) {
        toast.success('Expense added successfully!');
        resetForm();
        // Wait a moment and log again
        setTimeout(() => {
          console.log('📊 After create - Current expenses count:', expenses.length);
          console.log('📊 After create - All expenses:', allExpenses);
        }, 1000);
      } else {
        toast.error(`Failed to add expense: ${result.error || 'Unknown error'}`);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      category: 'Food & Supplies',
      description: '',
      amount: '',
      paymentMethod: 'cash',
      bookingId: '',
      bookingRef: '',
      notes: ''
    });
    setShowAddForm(false);
    setEditingExpense(null);
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      date: expense.date,
      category: expense.category,
      description: expense.description,
      amount: expense.amount.toString(),
      paymentMethod: expense.paymentMethod,
      bookingId: expense.bookingId || '',
      bookingRef: expense.bookingRef || '',
      notes: expense.notes || ''
    });
    setShowAddForm(true);
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      const result = await deleteExpense(id);
      if (result.success) {
        toast.success('Expense deleted successfully!');
      } else {
        toast.error(`Failed to delete expense: ${result.error || 'Unknown error'}`);
      }
    }
  };

  const handleBookingSelect = (e) => {
    const selectedId = e.target.value;
    const selectedJob = allJobs.find(j => j.id === selectedId);
    let bookingRef = '';
    if (selectedJob) {
      bookingRef = selectedJob.reference;
    }
    setFormData({
      ...formData,
      bookingId: selectedId,
      bookingRef
    });
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Category', 'Description', 'Amount', 'Payment Method', 'Booking', 'Notes'];
    const rows = expenses.map(exp => [
      exp.date,
      exp.category,
      exp.description,
      exp.amount,
      exp.paymentMethod,
      exp.bookingRef || 'Not tagged',
      exp.notes
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expenses_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="pf-page-wrap pb-32 lg:pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <DataFetchState
          loading={userDataLoading}
          error={userDataError}
          onRetry={refreshUserData}
          loadingLabel="Loading expenses"
          errorTitle="Couldn't load expenses"
          compact
        >
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Expense Tracker</h1>
          <p className="text-gray-600">Track your business expenses and link them to bookings for P&L analysis</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Expenses</p>
                <p className="text-2xl font-bold text-gray-900">₹{totalExpenses.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[var(--pf-accent)] flex items-center justify-center">
                <Tag className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Tagged to Bookings</p>
                <p className="text-2xl font-bold text-gray-900">₹{taggedExpenses.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-gray-500 to-gray-600 flex items-center justify-center">
                <IndianRupee className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-600">General Expenses</p>
                <p className="text-2xl font-bold text-gray-900">₹{untaggedExpenses.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-xl p-4 mb-6 border border-gray-200 shadow-sm">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-1 gap-4 w-full md:w-auto">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search expenses..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-[var(--pf-accent)]"
                />
              </div>

              {/* Category Filter */}
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-[var(--pf-accent)]"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-3">
              <button
                onClick={exportToCSV}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
              <button
                onClick={() => {
                  setEditingExpense(null);
                  setFormData({
                    date: new Date().toISOString().split('T')[0],
                    category: 'Food & Supplies',
                    description: '',
                    amount: '',
                    paymentMethod: 'cash',
                    bookingId: '',
                    bookingRef: '',
                    notes: ''
                  });
                  setShowAddForm(true);
                }}
                className="pf-btn-primary inline-flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-md"
              >
                <Plus className="w-5 h-5" />
                Add Expense
              </button>
            </div>
          </div>
        </div>

        {/* Add/Edit Form */}
        {showAddForm ? (
          <div className="bg-white rounded-xl p-6 mb-6 border-2 border-[var(--pf-accent)] shadow-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingExpense ? 'Edit Expense' : 'Add New Expense'}
              </h2>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setEditingExpense(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-[var(--pf-accent)]"
                    required
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-[var(--pf-accent)]"
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="What was this expense for?"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-[var(--pf-accent)]"
                    required
                  />
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount (₹) *
                  </label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-[var(--pf-accent)]"
                    required
                  />
                </div>

                {/* Payment Method */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method
                  </label>
                  <select
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-[var(--pf-accent)]"
                  >
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                    <option value="card">Card</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Tag to Job/Booking */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tag to Job/Booking (Optional)
                  </label>
                  <select
                    value={formData.bookingId}
                    onChange={handleBookingSelect}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-[var(--pf-accent)]"
                  >
                    <option value="">Not tagged to any job or booking</option>
                    {allJobs.map(job => (
                      <option key={job.id} value={job.id}>
                        {job.label} - {job.date ? new Date(job.date).toLocaleDateString() : 'No date'}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Link this expense to a booking or offline job for P&L tracking
                    {allJobs.length === 0 && ' (No jobs or bookings available yet)'}
                  </p>
                </div>

                {/* Notes */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional details..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-[var(--pf-accent)]"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingExpense(null);
                    resetForm();
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="pf-btn-primary inline-flex items-center gap-2 px-6 py-2 rounded-lg font-semibold"
                >
                  <Save className="w-4 h-4" />
                  {editingExpense ? 'Update Expense' : 'Add Expense'}
                </button>
              </div>
            </form>
          </div>
        ) : null}

        {!showAddForm && expenses.length === 0 && (
          <AnimatedEmptyState
            icon="loader-bowl-fill"
            title="Start tracking your expenses"
            description='Click "Add Expense" above to record your first business expense'
            className="mb-6"
          />
        )}

        {/* Expenses List */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Expense History</h2>
            <p className="text-sm text-gray-600 mt-1">
              {filteredExpenses.length} expense{filteredExpenses.length !== 1 ? 's' : ''} found
            </p>
          </div>

          <div className="divide-y divide-gray-200">
            {filteredExpenses.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <Receipt className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">No expenses found</p>
                <p className="text-sm">Start tracking your business expenses</p>
              </div>
            ) : (
              filteredExpenses.map((expense) => (
                <div key={expense.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-gray-900">{expense.description}</h3>
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                          {expense.category}
                        </span>
                        {expense.bookingRef && (
                          <span className="px-2 py-1 bg-[var(--pf-accent-soft)] text-[var(--pf-accent)] text-xs rounded-full flex items-center gap-1">
                            <Tag className="w-3 h-3" />
                            {expense.bookingRef}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(expense.date).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })}
                        </div>
                        <div className="flex items-center gap-1">
                          <IndianRupee className="w-4 h-4" />
                          {expense.paymentMethod}
                        </div>
                      </div>

                      {expense.notes && (
                        <p className="text-sm text-gray-600 mt-2">{expense.notes}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-2xl font-bold text-red-600">-₹{expense.amount.toLocaleString()}</p>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(expense)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit expense"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(expense.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete expense"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        </DataFetchState>
      </div>
    </div>
  );
};

export default ExpenseTracker;

